import React from 'react';
import { motion } from 'motion/react';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../common/SafeIcon';

const { LuSearch, LuImage, LuFolderHeart, LuLink, LuZap, LuSmartphone, LuClipboardCheck, LuMousePointerClick } = LuIcons;

interface Props {
  onComplete: () => void;
}

export function SplashScreen({ onComplete }: Props) {
  const capabilities = [
    {
      icon: LuZap,
      title: "Bulk Visualization",
      desc: "Instantly transform hundreds of raw URLs into a high-performance image gallery."
    },
    {
      icon: LuFolderHeart,
      title: "Local-First Privacy",
      desc: "Your data is stored securely in your browser's IndexedDB. No tracking, no middleman."
    },
    {
      icon: LuSmartphone,
      title: "Cloud Sync Ready",
      desc: "Seamlessly sync with Google Sheets using only a Web App URL for cross-device access."
    }
  ];

  const workflow = [
    {
      icon: LuLink,
      text: "Create an album and paste your list of image links into the workspace."
    },
    {
      icon: LuMousePointerClick,
      text: "Preview images in real-time with adjustable grid sizes for better visibility."
    },
    {
      icon: LuClipboardCheck,
      text: "Copy cleaned links to your clipboard or export the entire set as a .txt file."
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 1 }}
      exit={{ opacity: 0, scale: 1.05, filter: "blur(10px)" }}
      transition={{ duration: 0.8, ease: [0.4, 0, 0.2, 1] }}
      className="fixed inset-0 z-[100] bg-white flex items-center justify-center overflow-y-auto px-4 py-12"
    >
      <div className="max-w-5xl w-full flex flex-col items-center">
        {/* Header Section */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ scale: 0, rotate: -20 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 20, delay: 0.1 }}
            className="w-24 h-24 bg-blue-600 rounded-[2rem] flex items-center justify-center shadow-2xl shadow-blue-200 mx-auto mb-8"
          >
            <SafeIcon icon={LuSearch} className="w-12 h-12 text-white" />
          </motion.div>
          
          <motion.h1
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-6xl font-black text-slate-900 tracking-tighter mb-4"
          >
            LinkLens
          </motion.h1>
          
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-xl text-slate-500 font-medium max-w-2xl mx-auto leading-relaxed"
          >
            A high-performance workspace designed for developers, designers, and researchers to manage bulk image assets with absolute privacy.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 w-full items-start">
          {/* Capabilities Section */}
          <div className="space-y-8">
            <motion.h2 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.4 }}
              className="text-xs font-bold text-blue-600 uppercase tracking-[0.2em]"
            >
              Core Capabilities
            </motion.h2>
            <div className="space-y-6">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={i}
                  initial={{ x: -30, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.5 + (i * 0.1) }}
                  className="flex gap-5 group"
                >
                  <div className="w-12 h-12 bg-slate-50 border border-slate-100 rounded-2xl flex items-center justify-center shrink-0 group-hover:bg-blue-50 group-hover:border-blue-100 transition-colors">
                    <SafeIcon icon={cap.icon} className="w-6 h-6 text-slate-400 group-hover:text-blue-600 transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 mb-1">{cap.title}</h3>
                    <p className="text-sm text-slate-500 leading-relaxed">{cap.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Workflow Section */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-slate-900 rounded-[2.5rem] p-10 text-white shadow-3xl relative overflow-hidden"
          >
            {/* Background Accent */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/10 blur-[100px] pointer-events-none" />
            
            <h2 className="text-xs font-bold text-blue-400 uppercase tracking-[0.2em] mb-8">How to use LinkLens</h2>
            <div className="space-y-8">
              {workflow.map((step, i) => (
                <div key={i} className="flex gap-6 items-start">
                  <div className="relative">
                    <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center text-sm font-bold z-10 relative">
                      {i + 1}
                    </div>
                    {i !== workflow.length - 1 && (
                      <div className="absolute top-8 left-1/2 -translate-x-1/2 w-0.5 h-10 bg-slate-800" />
                    )}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className="text-slate-300 font-medium text-sm leading-relaxed">
                      {step.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer Action */}
        <div className="mt-16 text-center">
          <motion.button
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1 }}
            whileHover={{ scale: 1.02, backgroundColor: "#1d4ed8" }}
            whileTap={{ scale: 0.98 }}
            onClick={onComplete}
            className="px-12 py-5 bg-blue-600 text-white font-bold rounded-2xl shadow-xl shadow-blue-100 transition-all flex items-center gap-4 text-xl group"
          >
            I Understand & Acknowledge
            <SafeIcon icon={LuClipboardCheck} className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </motion.button>
          
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="mt-8 text-slate-400 text-sm font-medium flex items-center justify-center gap-2"
          >
            <SafeIcon icon={LuImage} className="w-4 h-4" />
            Optimized for large image datasets
          </motion.p>
        </div>
      </div>
    </motion.div>
  );
}